import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ApiError } from '@/lib/api';
import { getCourseCategoriesApi, setCourseCategoriesApi } from '@/lib/settings-api';
import { Card } from '@/components/ui/card';
import { ErrorState, LoadingState } from '@/components/ui/empty-state';
import { NameListEditor } from './name-list-editor';

/**
 * Issue #184 — KPI考核 → 课程分类管理。
 * 真实 GET/PATCH /system/course-categories（ADMIN+，按 orgId 隔离）。保留「细胞知识库」
 * + 企业文化 / 市场运营 / 后勤制度 / 行政HR / 实验室管理 / 财务管理，可增删，刷新后保持。
 */
const PRIMARY_BTN =
  'inline-flex items-center gap-2 rounded-md bg-brand-500 px-3 py-1.5 text-sm font-medium text-white transition hover:bg-brand-600 disabled:opacity-60';

export function CourseCategoriesSettingsCard({ canEdit }: { canEdit: boolean }) {
  const queryClient = useQueryClient();
  const query = useQuery({
    queryKey: ['system', 'course-categories'],
    queryFn: getCourseCategoriesApi,
  });
  const [items, setItems] = useState<string[] | null>(null);
  const [saved, setSaved] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  useEffect(() => {
    if (query.data) setItems(query.data);
  }, [query.data]);

  const mutation = useMutation({
    mutationFn: (next: string[]) => setCourseCategoriesApi(next),
    onSuccess: async (data) => {
      setSaved(true);
      setSaveError(null);
      setItems(data);
      await queryClient.invalidateQueries({ queryKey: ['system', 'course-categories'] });
    },
    onError: (err) =>
      setSaveError(err instanceof ApiError ? `${err.message}（HTTP ${err.status}）` : '保存失败，请重试。'),
  });

  if (query.isLoading) {
    return (
      <Card title="课程分类">
        <LoadingState label="正在加载课程分类…" />
      </Card>
    );
  }
  if (query.isError || items === null) {
    return (
      <Card title="课程分类">
        <ErrorState
          title="加载课程分类失败"
          description={query.error instanceof ApiError ? query.error.message : '请稍后重试。'}
          retry={() => void query.refetch()}
        />
      </Card>
    );
  }

  return (
    <Card
      title="课程分类"
      description="供知识库与课程归类使用。保留「细胞知识库」并可新增企业文化、市场运营、后勤制度、行政HR、实验室管理、财务管理等分类。"
    >
      <NameListEditor
        items={items}
        onChange={(next) => {
          setSaved(false);
          setItems(next);
        }}
        disabled={!canEdit}
        placeholder="输入分类名称后回车添加"
        emptyHint="暂无分类，请至少添加 1 项"
      />
      {canEdit ? (
        <div className="mt-4 flex items-center gap-3">
          <button
            className={PRIMARY_BTN}
            onClick={() => {
              setSaved(false);
              setSaveError(null);
              mutation.mutate(items);
            }}
            disabled={mutation.isPending || items.length === 0}
          >
            {mutation.isPending ? '保存中…' : '保存分类'}
          </button>
          {saved ? <span className="text-xs text-emerald-600">已保存，刷新后保持。</span> : null}
        </div>
      ) : (
        <p className="mt-3 text-xs text-slate-500">仅 ADMIN 及以上角色可修改课程分类。</p>
      )}
      {saveError ? (
        <div className="mt-3 rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-700">{saveError}</div>
      ) : null}
    </Card>
  );
}
